import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { List, Box, ListItemText, Collapse, IconButton } from '@mui/material';
import { useSelector } from 'react-redux';
import { 
  ExitToApp as ExitToAppIcon, 
  ChevronLeft, 
  ChevronRight
} from '@mui/icons-material';
import SidebarLogo from '../../assets/main/sidebar-logo.png'
import DashboardLogo from '../../assets/sidebar/dashboard-icon.svg'
import SettingsLogo from '../../assets/sidebar/settings.svg'
import SubscriptionLogo from '../../assets/sidebar/subscriptions.svg'
import UserManagementLogo from '../../assets/sidebar/user-management.svg'
import SystemManagementLogo from '../../assets/sidebar/system-management.svg'
import OrganisationLogo from '../../assets/sidebar/organisation.svg'
import ContactRequestLogo from '../../assets/sidebar/contact-request.svg'
import { useSidebar } from '../../context/SidebarContext';
import { selectUserPermissions } from '../../modules/user/selectors';

import {
  LogoContainer,
  LogoText,
  OrgText,
  StyledDrawer,
  StyledListItemButton,
  StyledListItemIcon,
  ChildItem
} from './styles';

interface SidebarProps {
  onLogout: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ onLogout }) => {
  const { activePage, isCollapsed, toggleSidebar } = useSidebar();
  const [isSystemManagementOpen, setSystemManagementOpen] = useState(false);
  const [isMasterDataOpen, setMasterDataOpen] = useState(false);
  const permissions = useSelector(selectUserPermissions);

  const hasReadPermission = (moduleSlug: string) => {
    return permissions[moduleSlug]?.have_read || permissions[moduleSlug]?.have_admin;
  };

  const isUserActive = activePage === 'user-role-mapping';
  const isSystemActive = activePage === 'system-roles' || activePage === 'system-modules' || activePage === 'role-module-mapping';

  const showDashboard = hasReadPermission('super-admin-dashboard');
  const showUserManagement = hasReadPermission('user-management');
  const showSystemRoles = hasReadPermission('system-roles');
  const showSystemModules = hasReadPermission('system-modules');
  const showRoleMapping = hasReadPermission('role-module-mapping');
  const showDocumentTypes = hasReadPermission('document-types');
  const showSignupForm = hasReadPermission('signup-form');
  const showSubscriptions = hasReadPermission('subscriptions');
  const showOrganizations = hasReadPermission('organizations');
  const showContactRequests = hasReadPermission('contact-management');
  const showSystemManagement = showSystemRoles || showSystemModules || showRoleMapping;
  const showMasterData = showDocumentTypes || showSignupForm;

  return (
    <>
      <LogoContainer collapsed={isCollapsed}>
        <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', justifyContent: isCollapsed ? 'center' : 'flex-start' }}>
          <img src={SidebarLogo} alt="Logo" style={{ height: '32px', width: '32px' }} />
          {!isCollapsed && (
            <Box sx={{ ml: 2, display: 'flex', flexDirection: 'column' }}>
              <LogoText>Talent Hub</LogoText>
              <OrgText variant="caption">SuperAdmin</OrgText>
            </Box>
          )}
        </Box>

        <Box sx={{ position: 'absolute', right: -12, top: 20, zIndex: 1300 }}>
          <IconButton
            onClick={toggleSidebar}
            size="small"
            sx={{
              backgroundColor: '#fff',
              border: '1px solid #e0e0e0',
              width: 24,
              height: 24,
              '&:hover': { backgroundColor: '#f5f5f5' }
            }}
          >
            {isCollapsed ? <ChevronRight fontSize="small" /> : <ChevronLeft fontSize="small" />}
          </IconButton>
        </Box>
      </LogoContainer>

      <StyledDrawer variant="permanent" collapsed={isCollapsed}>
        <List>
          {/* Dashboard */}
          {showDashboard && (
            <Link to="/dashboard" style={{ textDecoration: 'none', color: 'inherit' }}>
              <StyledListItemButton selected={activePage === 'dashboard'}>
                <StyledListItemIcon>
                  <img src={DashboardLogo} alt="Dashboard" style={{ height: '16px', width: '16px' }} />
                </StyledListItemIcon>
                {!isCollapsed && <ListItemText primary="Dashboard" />}
              </StyledListItemButton>
            </Link>
          )}

          {/* User Management */}
          {showUserManagement && (
            <Link to="/super-admin/user-role-mapping" style={{ textDecoration: 'none', color: 'inherit' }}>
              <StyledListItemButton selected={isUserActive}>
                <StyledListItemIcon>
                  <img src={UserManagementLogo} alt="User Management" style={{ height: '16px', width: '16px' }} />
                </StyledListItemIcon>
                {!isCollapsed && <ListItemText primary="User Management" />}
              </StyledListItemButton>
            </Link>
          )}

          {/* System Management */}
          {showSystemManagement && (
            <>
              <StyledListItemButton
                selected={isSystemActive}
                onClick={() => setSystemManagementOpen(!isSystemManagementOpen)}
              >
                <StyledListItemIcon>
                  <img src={SystemManagementLogo} alt="System" style={{ height: '16px', width: '16px' }} />
                </StyledListItemIcon>
                {!isCollapsed && <ListItemText primary="System Management" />}
              </StyledListItemButton>

              <Collapse in={!isCollapsed && (isSystemManagementOpen || isSystemActive)} timeout="auto" unmountOnExit>
                <List component="div" disablePadding>
                  {showSystemRoles && (
                    <Link to="/super-admin/system-roles" style={{ textDecoration: 'none', color: 'inherit' }}>
                      <ChildItem selected={activePage === 'system-roles'}>
                        <Box sx={{ width: '6px', height: '6px', bgcolor: 'text.primary', mr: 2 }} />
                        <ListItemText primary="System Roles" />
                      </ChildItem>
                    </Link>
                  )}
                  {showSystemModules && (
                    <Link to="/super-admin/system-modules" style={{ textDecoration: 'none', color: 'inherit' }}>
                      <ChildItem selected={activePage === 'system-modules'}>
                        <Box sx={{ width: '6px', height: '6px', bgcolor: 'text.primary', mr: 2 }} />
                        <ListItemText primary="System Modules" />
                      </ChildItem>
                    </Link>
                  )}
                  {showRoleMapping && (
                    <Link to="/super-admin/role-module-mapping" style={{ textDecoration: 'none', color: 'inherit' }}>
                      <ChildItem selected={activePage === 'role-module-mapping'}>
                        <Box sx={{ width: '6px', height: '6px', bgcolor: 'text.primary', mr: 2 }} />
                        <ListItemText primary="Role Module Mapping" />
                      </ChildItem>
                    </Link>
                  )}
                </List>
              </Collapse>
            </>
          )}
          
          {/* Subscriptions */}
          {showSubscriptions && (
            <Link to="/super-admin/subscriptions" style={{ textDecoration: 'none', color: 'inherit' }}>
              <StyledListItemButton selected={activePage === 'subscriptions'}>
                <StyledListItemIcon>
                  <img src={SubscriptionLogo} alt="Subscriptions" style={{ height: '16px', width: '16px' }} />
                </StyledListItemIcon>
                {!isCollapsed && <ListItemText primary="Subscriptions" />}
              </StyledListItemButton>
            </Link>
          )}

          {/* Organizations */}
          {showOrganizations && (
            <Link to="/super-admin/organizations" style={{ textDecoration: 'none', color: 'inherit' }}>
              <StyledListItemButton selected={activePage === 'organizations'}>
                <StyledListItemIcon>
                  <img src={OrganisationLogo} alt="Organizations" style={{ height: '16px', width: '16px' }} />
                </StyledListItemIcon>
                {!isCollapsed && <ListItemText primary="Organizations" />}
              </StyledListItemButton>
            </Link>
          )}

          {/* Contact Requests */}
          {showContactRequests && (
            <Link to="/super-admin/contact-requests" style={{ textDecoration: 'none', color: 'inherit' }}>
              <StyledListItemButton selected={activePage === 'contact-requests'}>
                <StyledListItemIcon>
                  <img src={ContactRequestLogo} alt="Contact Requests" style={{ height: '16px', width: '16px' }} />
                </StyledListItemIcon>
                {!isCollapsed && <ListItemText primary="Contact Requests" />}
              </StyledListItemButton>
            </Link>
          )}

          {/* Master Data */}
          {showMasterData && (
            <>
              <StyledListItemButton
                selected={activePage === 'document-types'}
                onClick={() => setMasterDataOpen(!isMasterDataOpen)}
              >
                <StyledListItemIcon>
                  <img src={SettingsLogo} alt="Settings" style={{ height: '16px', width: '16px' }} />
                </StyledListItemIcon>
                {!isCollapsed && <ListItemText primary="Settings" />}
              </StyledListItemButton>

              <Collapse in={!isCollapsed && (isMasterDataOpen || activePage === 'document-types')} timeout="auto" unmountOnExit>
                <List component="div" disablePadding>
                  {showDocumentTypes && (
                    <Link to="/super-admin/masterdata/document-types" style={{ textDecoration: 'none', color: 'inherit' }}>
                      <ChildItem selected={activePage === 'document-types'}>
                        <Box sx={{ width: '6px', height: '6px', bgcolor: 'text.primary', mr: 2 }} />
                        <ListItemText primary="Document Types" />
                      </ChildItem>
                    </Link>
                  )}
                  {showSignupForm && (
                    <Link to="/super-admin/masterdata/signup-form" style={{ textDecoration: 'none', color: 'inherit' }}>
                      <ChildItem selected={activePage === 'signup-form'}>
                        <Box sx={{ width: '6px', height: '6px', bgcolor: 'text.primary', mr: 2 }} />
                        <ListItemText primary="Signup Form" />
                      </ChildItem>
                    </Link>
                  )}
                </List>
              </Collapse>
            </>
          )}
        </List>

        <List sx={{ mt: 'auto' }}>
          <StyledListItemButton onClick={onLogout}>
            <StyledListItemIcon> <ExitToAppIcon /> </StyledListItemIcon>
            {!isCollapsed && <ListItemText primary="Logout" />}
          </StyledListItemButton>
        </List>
      </StyledDrawer>
    </>
  );
};

export default Sidebar;